import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Check, Crown, Zap, ArrowLeft, Lock, Loader2, Sparkles, CreditCard, QrCode, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { trackInitiateCheckout, trackViewContent } from '@/lib/metaPixel';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(null); // 'basic' or 'premium'
  const [showPaymentModal, setShowPaymentModal] = useState(null); // plan id

  // Track page view for Meta Pixel
  useEffect(() => {
    trackViewContent('Pricing Page', 'Plans');
    trackInitiateCheckout({ content_category: 'subscription' });
  }, []);

  // Get user's current subscription type
  const userPlan = user?.subscription_type || 'free';

  // Check if user has active subscription for a plan
  const isSubscribed = (planId) => {
    if (!user) return false;
    return userPlan === planId;
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info('Faça login para assinar');
      login();
      return;
    }

    // If already subscribed to this plan, just navigate
    if (isSubscribed(planId)) {
      if (planId === 'free') {
        navigate('/analyze');
      } else {
        toast.info('Você já possui este plano ativo!');
      }
      return;
    }

    if (planId === 'free') {
      navigate('/analyze');
      return;
    }

    // Show payment method selection modal
    setShowPaymentModal(planId);
  };

  const handlePaymentMethodSelect = async (planId, method) => {
    setShowPaymentModal(null);
    setLoading(planId);

    try {
      if (method === 'pix') {
        // Navigate to PIX payment page
        navigate(`/pix?package=${planId}_monthly`);
      } else {
        // Create Stripe checkout session (card/boleto)
        const sessionToken = localStorage.getItem('session_token');
        const response = await axios.post(`${API}/payments/checkout`, {
          package_id: `${planId}_monthly`,
          origin_url: window.location.origin
        }, { 
          withCredentials: true,
          headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}
        });

        // Redirect to Stripe checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erro ao processar. Tente novamente.');
      setLoading(null);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      price: 'R$ 0',
      priceNote: 'para começar',
      description: 'Experimente antes de assinar',
      features: [
        '1 análise gratuita',
        '7 dias grátis do organizador de despesas',
        'Classificação de risco com IA',
        'Alertas personalizados',
        'Alternativas seguras'
      ],
      notIncluded: [
        'Análises adicionais',
        'Histórico completo',
        'Categorias e comparativos',
        'Exportar PDF'
      ],
      cta: 'Fazer Análise Grátis',
      highlight: false,
      badge: null
    },
    {
      id: 'basic',
      name: 'Básico',
      price: 'R$ 9,90',
      priceNote: 'por mês',
      description: 'Para quem precisa analisar regularmente',
      features: [
        '5 análises por mês',
        'Classificação de risco com IA',
        'Alertas personalizados',
        'Alternativas seguras',
        'Histórico de análises',
        'Exportar PDF'
      ],
      notIncluded: [
        'Análises ilimitadas',
        'Organizador de despesas',
        'Gráficos e comparativos',
        'Metas de economia'
      ],
      cta: 'Assinar Básico',
      highlight: false,
      badge: null
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 'R$ 29,90',
      priceNote: 'por mês',
      description: 'Proteção financeira completa',
      features: [
        'Análises ilimitadas',
        'Classificação de risco com IA',
        'Alertas personalizados',
        'Alternativas seguras',
        'Histórico completo',
        'Exportar PDF',
        'Organizador de despesas completo',
        'Gráficos por categoria',
        'Alertas de limite de gastos',
        'Meta de economia mensal',
        'Comparativo mês a mês',
        'Categorias personalizáveis',
        'Guia de investimentos',
        'Simulador de investimentos',
        'Suporte prioritário'
      ],
      notIncluded: [],
      cta: 'Assinar Premium',
      highlight: true,
      badge: 'Mais Popular'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-sm font-medium mb-6">
              <Crown className="w-4 h-4" />
              Escolha seu plano
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Proteja suas Finanças
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comece grátis e faça upgrade quando precisar de mais análises
            </p>
          </motion.div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                className={`relative rounded-3xl p-6 ${
                  plan.highlight 
                    ? 'bg-gradient-to-b from-[#00FF94]/10 to-[#0A0A0A] border-2 border-[#00FF94]/50 scale-105 z-10' 
                    : 'bg-[#0A0A0A] border border-white/10'
                }`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Subscribed Badge */}
                {isSubscribed(plan.id) && plan.id !== 'free' && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-[#00FF94] text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Seu Plano
                    </span>
                  </div>
                )}

                {plan.badge && !isSubscribed(plan.id) && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#00FF94] text-black text-sm font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <h2 className="text-xl font-bold text-white mb-1">{plan.name}</h2>
                  <p className="text-gray-500 text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-black text-white">{plan.price}</span>
                  <span className="text-gray-500 ml-2">/{plan.priceNote}</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#00FF94]" />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 opacity-40">
                      <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <Lock className="w-3 h-3 text-gray-500" />
                      </div>
                      <span className="text-gray-500 text-sm line-through">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  className={`w-full rounded-full font-bold py-5 ${
                    isSubscribed(plan.id) && plan.id !== 'free'
                      ? 'bg-[#00FF94]/20 text-[#00FF94] border-2 border-[#00FF94] cursor-default'
                      : plan.highlight
                        ? 'bg-[#00FF94] text-black hover:bg-[#00CC76] glow-primary'
                        : plan.id === 'free' 
                          ? 'bg-white/10 text-white hover:bg-white/20'
                          : 'bg-white/5 text-white hover:bg-white/10 border border-white/20'
                  }`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading !== null || (isSubscribed(plan.id) && plan.id !== 'free')}
                  data-testid={`${plan.id}-plan-btn`}
                >
                  {loading === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : isSubscribed(plan.id) && plan.id !== 'free' ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Assinado
                    </>
                  ) : (
                    plan.cta
                  )}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Comparison Note */}
          <motion.div
            className="mt-12 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#00FF94]" />
                Por que assinar?
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400 mb-2">
                    <span className="text-white font-medium">Básico:</span> Ideal para quem precisa avaliar decisões financeiras regularmente, 
                    como compras parceladas ou empréstimos.
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">
                    <span className="text-white font-medium">Premium:</span> Para quem quer controle total das finanças, 
                    com organizador de despesas, metas e análises ilimitadas.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-gray-500 text-sm">
              Pagamento seguro via Stripe e Mercado Pago • Cancele quando quiser • Sem compromisso
            </p>
          </motion.div>
        </div>
      </main>

      {/* Payment Method Selection Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPaymentModal(null)}
          >
            <motion.div
              className="bg-[#0A0A0A] rounded-2xl p-6 max-w-md w-full border border-white/10"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Escolha como pagar</h3>
                <button
                  onClick={() => setShowPaymentModal(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Plan Info */}
              <div className="bg-[#141414] rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    Plano {showPaymentModal === 'basic' ? 'Básico' : 'Premium'}
                  </span>
                  <span className="text-xl font-bold text-[#00FF94]">
                    R$ {showPaymentModal === 'basic' ? '9,90' : '29,90'}/mês
                  </span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                {/* PIX Option */}
                <button
                  onClick={() => handlePaymentMethodSelect(showPaymentModal, 'pix')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#141414] border border-white/5 hover:border-[#00FF94]/50 hover:bg-[#00FF94]/5 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#00FF94]/10 flex items-center justify-center group-hover:bg-[#00FF94]/20 transition-colors">
                    <QrCode className="w-6 h-6 text-[#00FF94]" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-white">PIX</div>
                    <div className="text-sm text-gray-400">Pagamento instantâneo</div>
                  </div>
                  <div className="text-xs text-[#00FF94] font-medium bg-[#00FF94]/10 px-2 py-1 rounded-full">
                    Recomendado
                  </div>
                </button>

                {/* Card/Boleto Option */}
                <button
                  onClick={() => handlePaymentMethodSelect(showPaymentModal, 'stripe')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#141414] border border-white/5 hover:border-white/20 hover:bg-white/5 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/15 transition-colors">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-white">Cartão ou Boleto</div>
                    <div className="text-sm text-gray-400">Crédito, débito ou boleto bancário</div>
                  </div>
                </button>
              </div>

              {/* Security Note */}
              <div className="mt-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
                <Lock className="w-4 h-4" />
                <span>Pagamento 100% seguro</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <QuickNav />
    </div>
  );
};

export default PricingPage;
